import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Drawer, Fab, IconButton, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import PivotTableChartIcon from "@mui/icons-material/PivotTableChart";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import { listCompanySummaryData } from "../api/underwritingApi";
import { getBulkUploadProgress } from "../api/companyApi";
import { runClaudeChatJob, streamClaudeChat } from "../api/claudeApi";

const BLUE = "#5186EC";
const INK = "#16284a";
// Gemini Pro's long context can safely carry the complete saved portfolio used
// by the distributor dashboard. Keep a high guardrail for unusually large
// tenants, but do not sample a normal 1,000-company portfolio.
const PORTFOLIO_CONTEXT_BUDGET = 1000000;
const UPLOAD_SAMPLE_ROWS = 500;
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;
const num = (value) => {
  const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};
const pick = (data, ...keys) => {
  for (const key of keys) if (data?.[key] !== undefined && data?.[key] !== null && data?.[key] !== "") return data[key];
  return null;
};
export const hasPortfolioChatContext = (rows, files, supplementalContext) => (
  (rows || []).length > 0
  || (files || []).length > 0
  || Boolean(supplementalContext && Object.keys(supplementalContext).length)
);
const isoTimestamp = (value) => {
  if (!value) return null;
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000).toISOString();
  if (typeof value?._seconds === "number") return new Date(value._seconds * 1000).toISOString();
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null;
};
const normalizeCountry = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw.toLowerCase().replace(/[^a-z]/g, "");
  const aliases = {
    ca: "Canada", can: "Canada", canada: "Canada",
    gb: "United Kingdom", gbr: "United Kingdom", greatbritain: "United Kingdom",
    uk: "United Kingdom", unitedkingdom: "United Kingdom",
    us: "United States", usa: "United States", unitedstates: "United States",
    unitedstatesofamerica: "United States",
  };
  return aliases[key] || raw;
};
export const brandArcAI = (text) => String(text || "")
  .replace(/\b(?:Claude|Anthropic|Gemini|ChatGPT|OpenAI)\b/gi, "ArcAI");
export const portfolioScopeContract = (scope) => scope === "ar"
  ? "This is the Account Receivable portfolio. AR balances and aging buckets are in scope; prospect requested-limit fields may be absent and must not be treated as an AR data-quality defect."
  : "This is the Prospects portfolio. AR balances and aging buckets are out of scope and may be zero by design; never describe those zeros as missing payment history, an AR data-quality defect, or evidence of no payment visibility.";

const median = (values) => {
  const sorted = (values || []).map(num).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const mixSummary = (rows, key, missingLabel = "Missing / unavailable") => Object.values(rows.reduce((out, row) => {
  const label = row[key] || missingLabel;
  if (!out[label]) out[label] = { label, companies: 0, shortfall: 0, recommendedExposure: 0 };
  out[label].companies += 1;
  out[label].shortfall += Math.max(0, num(row.amountRequested) - num(row.recommendedLimit));
  out[label].recommendedExposure += num(row.recommendedLimit);
  return out;
}, {})).sort((a, b) => b.shortfall - a.shortfall || b.recommendedExposure - a.recommendedExposure || a.label.localeCompare(b.label));

export function buildAdvancedPortfolioAnalytics(rows) {
  const allRows = rows || [];
  const totalCapacity = allRows.reduce((sum, row) => sum + num(row.recommendedLimit), 0);
  const totalShortfall = allRows.reduce((sum, row) => sum + Math.max(0, num(row.amountRequested) - num(row.recommendedLimit)), 0);
  const bandOrder = ["Low", "Medium", "High", "Missing"];
  const bandStats = bandOrder.map((band) => {
    const members = allRows.filter((row) => riskBand(row.arcScore) === band);
    const restricted = members.filter((row) => row.amountRequested > 0 && row.recommendedLimit < row.amountRequested);
    const capacity = members.reduce((sum, row) => sum + num(row.recommendedLimit), 0);
    const shortfall = restricted.reduce((sum, row) => sum + row.amountRequested - row.recommendedLimit, 0);
    const top10Capacity = [...members]
      .sort((a, b) => b.recommendedLimit - a.recommendedLimit)
      .slice(0, 10)
      .reduce((sum, row) => sum + num(row.recommendedLimit), 0);
    const companySharePct = allRows.length ? members.length / allRows.length * 100 : 0;
    const capacitySharePct = totalCapacity ? capacity / totalCapacity * 100 : 0;
    return {
      riskBand: band,
      companies: members.length,
      companySharePct,
      recommendedExposure: capacity,
      capacitySharePct,
      allocationGapPctPoints: capacitySharePct - companySharePct,
      requestedDemand: members.reduce((sum, row) => sum + num(row.amountRequested), 0),
      restrictedCompanies: restricted.length,
      requestedLimitShortfall: shortfall,
      shortfallSharePct: totalShortfall ? shortfall / totalShortfall * 100 : 0,
      averageShortfallPerRestrictedCompany: restricted.length ? shortfall / restricted.length : null,
      averageRecommendedLimit: members.length ? capacity / members.length : null,
      medianRecommendedLimit: median(members.map((row) => row.recommendedLimit)),
      top10RecommendedExposure: top10Capacity,
      top10ShareWithinBandPct: capacity ? top10Capacity / capacity * 100 : 0,
    };
  });

  const classificationDimensions = [
    { field: "sector", label: "Sector / industry" },
    { field: "arcScore", label: "ARC score", missing: (row) => row.arcScore === null },
    { field: "country", label: "Country" },
  ].map(({ field, label, missing }) => {
    const members = allRows.filter((row) => missing ? missing(row) : !row[field]);
    const exposure = members.reduce((sum, row) => sum + num(row.recommendedLimit), 0);
    return {
      field: label,
      companiesMissing: members.length,
      recommendedExposure: exposure,
      capacitySharePct: totalCapacity ? exposure / totalCapacity * 100 : 0,
    };
  });
  const incompletelyClassified = allRows.filter((row) => !row.sector || row.arcScore === null || !row.country);
  const completelyClassified = allRows.filter((row) => row.sector && row.arcScore !== null && row.country);
  const incompleteExposure = incompletelyClassified.reduce((sum, row) => sum + num(row.recommendedLimit), 0);

  const sectorCompletenessRiskMatrix = [];
  ["Classified sector", "Missing sector"].forEach((sectorCompleteness) => {
    bandOrder.forEach((band) => {
      const members = allRows.filter((row) =>
        riskBand(row.arcScore) === band
        && (sectorCompleteness === "Classified sector" ? Boolean(row.sector) : !row.sector));
      sectorCompletenessRiskMatrix.push({
        sectorCompleteness,
        riskBand: band,
        companies: members.length,
        recommendedExposure: members.reduce((sum, row) => sum + num(row.recommendedLimit), 0),
        requestedLimitShortfall: members.reduce((sum, row) => sum + Math.max(0, row.amountRequested - row.recommendedLimit), 0),
      });
    });
  });

  const topRequestedLimitGaps = [...allRows]
    .map((row) => ({ ...row, requestedLimitGap: Math.max(0, row.amountRequested - row.recommendedLimit) }))
    .filter((row) => row.requestedLimitGap > 0)
    .sort((a, b) => b.requestedLimitGap - a.requestedLimitGap || b.amountRequested - a.amountRequested || a.company.localeCompare(b.company))
    .slice(0, 200)
    .map((row) => ({
      company: row.company,
      companyId: row.companyId,
      amountRequested: row.amountRequested,
      recommendedLimit: row.recommendedLimit,
      requestedLimitGap: row.requestedLimitGap,
      arcScore: row.arcScore,
      riskBand: riskBand(row.arcScore),
      sector: row.sector || "Missing / unavailable",
      country: row.country || "Missing / unavailable",
    }));
  const top15Rows = topRequestedLimitGaps.slice(0, 15);
  const top15Shortfall = top15Rows.reduce((sum, row) => sum + row.requestedLimitGap, 0);
  const top15RequestedLimitGapCohort = {
    records: top15Rows,
    combinedShortfall: top15Shortfall,
    shareOfPortfolioShortfallPct: totalShortfall ? top15Shortfall / totalShortfall * 100 : 0,
    riskBandMix: mixSummary(top15Rows, "riskBand"),
    sectorMix: mixSummary(top15Rows, "sector"),
    countryMix: mixSummary(top15Rows, "country"),
  };

  const outlierRecords = allRows
    .filter((row) =>
      (row.recommendedLimit >= 1000000 && row.arcScore !== null && row.arcScore >= 8)
      || (row.recommendedLimit === 0 && row.amountRequested >= 5000000))
    .map((row) => ({
      company: row.company,
      amountRequested: row.amountRequested,
      recommendedLimit: row.recommendedLimit,
      arcScore: row.arcScore,
      riskBand: riskBand(row.arcScore),
      matchedRules: [
        ...(row.recommendedLimit >= 1000000 && row.arcScore !== null && row.arcScore >= 8 ? ["recommendedLimit >= 1000000 and ARC >= 8"] : []),
        ...(row.recommendedLimit === 0 && row.amountRequested >= 5000000 ? ["recommendedLimit = 0 and amountRequested >= 5000000"] : []),
      ],
    }))
    .sort((a, b) => b.amountRequested - a.amountRequested || b.recommendedLimit - a.recommendedLimit || a.company.localeCompare(b.company));
  const top10PortfolioRows = [...allRows].sort((a, b) => b.recommendedLimit - a.recommendedLimit).slice(0, 10);
  const top10PortfolioExposure = top10PortfolioRows.reduce((sum, row) => sum + num(row.recommendedLimit), 0);
  const sectorConcentrations = Object.values(allRows.reduce((out, row) => {
    const label = row.sector || "Missing / unavailable";
    if (!out[label]) out[label] = { sector: label, companies: 0, recommendedExposure: 0 };
    out[label].companies += 1;
    out[label].recommendedExposure += num(row.recommendedLimit);
    return out;
  }, {})).map((row) => ({
    ...row,
    capacitySharePct: totalCapacity ? row.recommendedExposure / totalCapacity * 100 : 0,
  })).sort((a, b) => b.recommendedExposure - a.recommendedExposure || a.sector.localeCompare(b.sector));

  const medium = bandStats.find((row) => row.riskBand === "Medium");
  const high = bandStats.find((row) => row.riskBand === "High");
  const capacityLoss = num(medium?.recommendedExposure) * .10 + num(high?.recommendedExposure) * .25;
  const stressedCapacity = totalCapacity - capacityLoss;
  const differentiatedRiskStress = {
    assumptions: [
      "High (ARC >= 8): reduce recommended limits by 25%",
      "Medium (ARC >= 4 and < 8): reduce recommended limits by 10%",
      "Low (ARC < 4) and Missing ARC: unchanged",
      "Requested demand: unchanged",
    ],
    baselineCapacity: totalCapacity,
    capacityLoss,
    stressedCapacity,
    stressedBandResults: bandStats.map((row) => {
      const multiplier = row.riskBand === "High" ? .75 : row.riskBand === "Medium" ? .90 : 1;
      const exposure = row.recommendedExposure * multiplier;
      return {
        riskBand: row.riskBand,
        stressedRecommendedExposure: exposure,
        stressedCapacitySharePct: stressedCapacity ? exposure / stressedCapacity * 100 : 0,
      };
    }),
  };

  return {
    methodology: "Calculated deterministically from every saved portfolio row before the model call. Each company is counted once per stated grouping; missing values remain explicit.",
    totalCompanies: allRows.length,
    totalRecommendedCapacity: totalCapacity,
    totalRequestedLimitShortfall: totalShortfall,
    riskBandStats: bandStats,
    classificationBlindSpots: {
      dimensions: classificationDimensions,
      incompleteAnyOfSectorArcCountry: {
        companies: incompletelyClassified.length,
        recommendedExposure: incompleteExposure,
        capacitySharePct: totalCapacity ? incompleteExposure / totalCapacity * 100 : 0,
      },
      completeAcrossSectorArcCountry: {
        companies: completelyClassified.length,
        recommendedExposure: completelyClassified.reduce((sum, row) => sum + num(row.recommendedLimit), 0),
      },
      noDoubleCountingMethod: "Union of company rows missing sector OR ARC score OR country; each company and its recommended limit are counted once.",
    },
    sectorCompletenessRiskMatrix,
    sectorConcentrations,
    top10PortfolioConcentration: {
      records: top10PortfolioRows.map((row) => ({ company: row.company, recommendedLimit: row.recommendedLimit, arcScore: row.arcScore })),
      recommendedExposure: top10PortfolioExposure,
      capacitySharePct: totalCapacity ? top10PortfolioExposure / totalCapacity * 100 : 0,
    },
    topRequestedLimitGaps,
    top15RequestedLimitGapCohort,
    differentiatedRiskStress,
    outlierReviewAtSpecifiedThresholds: {
      rules: ["recommendedLimit >= 1000000 and ARC >= 8", "recommendedLimit = 0 and amountRequested >= 5000000"],
      records: outlierRecords,
      companies: outlierRecords.length,
      totalRecommendedExposure: outlierRecords.reduce((sum, row) => sum + row.recommendedLimit, 0),
      totalRequestedDemand: outlierRecords.reduce((sum, row) => sum + row.amountRequested, 0),
    },
  };
}

export function compactPortfolio(raw, scope) {
  const all = (raw || []).map((record) => {
    const data = typeof record.summaryData === "string" ? JSON.parse(record.summaryData || "{}") : (record.summaryData || {});
    const balance = num(pick(data, "Total AR Balance", "TotalARBalance"));
    const current = num(pick(data, "Current AR Balance", "Current"));
    const aging30 = num(pick(data, "AR Balance Aging 30", "Aging 30"));
    const aging60 = num(pick(data, "AR Balance Aging 60", "Aging 60"));
    const aging90 = num(pick(data, "AR Balance Aging 90", "Aging 90"));
    const total = balance || current + aging30 + aging60 + aging90;
    const isAr = record.isArCustomer === true || total !== 0;
    const score = num(pick(data, "Combined Score", "ARC Score"));
    const requestedValue = pick(data, "Amount Requested", "Requested Limit", "Credit Limit Requested");
    const recommendedValue = pick(data, "Recommended Limit");
    return {
      companyId: record.companyId,
      company: pick(data, "Company Name", "companyName") || record.companyName || "Unknown",
      country: normalizeCountry(pick(data, "Country", "country") || record.country),
      sector: pick(data, "Sector", "sector", "Industry", "industry") || record.sector || record.industry || "",
      arcScore: score >= 1 && score <= 10 ? score : null,
      externalScore: num(pick(data, "External Score")) || null,
      paymentIndex: num(pick(data, "Payment Index")) || null,
      amountRequested: num(requestedValue) || num(record.requestedLimit),
      recommendedLimit: num(recommendedValue),
      hasRequestedLimit: requestedValue !== null || record.requestedLimit !== undefined,
      hasRecommendedLimit: recommendedValue !== null,
      totalArBalance: total,
      current, aging30, aging60, aging90,
      rationale: pick(data, "Underwriting Reason", "Rationale") || "",
      lastUpdatedAt: isoTimestamp(pick(data, "Last Updated", "Updated At", "Assessment Date") || record.underwritingTiming?.at || record.updatedAt || record.createdAt),
      sourceSystem: pick(data, "Source System", "Data Source") || record.sourceSystem || "",
      underwritingInProgress: record.underwritingInProgress === true,
      isAr,
    };
  });
  const rows = all.filter((row) => scope === "ar" ? row.isAr : !row.isAr);
  const sum = (key) => rows.reduce((total, row) => total + num(row[key]), 0);
  const bands = rows.reduce((out, row) => {
    if (row.arcScore === null) { out.missing += 1; return out; }
    const key = row.arcScore >= 8 ? "high" : row.arcScore >= 4 ? "medium" : "low";
    out[key] += 1;
    return out;
  }, { low: 0, medium: 0, high: 0, missing: 0 });
  const scoredRows = rows.filter((row) => row.arcScore !== null);
  const requestedRows = rows.filter((row) => row.amountRequested > 0);
  const belowRequested = requestedRows.filter((row) => row.recommendedLimit < row.amountRequested);
  const aggregates = {
      scope, companies: rows.length, totalArBalance: sum("totalArBalance"),
      recommendedLimit: sum("recommendedLimit"), current: sum("current"), aging30: sum("aging30"),
      aging60: sum("aging60"), aging90: sum("aging90"), riskBands: bands,
      averageArc: scoredRows.length ? scoredRows.reduce((total, row) => total + row.arcScore, 0) / scoredRows.length : 0,
      companiesWithArcScore: scoredRows.length,
      companiesMissingArcScore: rows.length - scoredRows.length,
      companiesWithRequestedLimit: requestedRows.length,
      companiesBelowRequestedLimit: belowRequested.length,
      companiesMeetingRequestedLimit: requestedRows.length - belowRequested.length,
      totalRequestedLimit: sum("amountRequested"),
      requestedLimitShortfall: belowRequested.reduce((total, row) => total + row.amountRequested - row.recommendedLimit, 0),
      companiesMissingCountry: rows.filter((row) => !row.country).length,
      companiesMissingSector: rows.filter((row) => !row.sector).length,
      companiesMissingUpdatedAt: rows.filter((row) => !row.lastUpdatedAt).length,
  };
  return { rows, aggregates, advancedAnalytics: buildAdvancedPortfolioAnalytics(rows) };
}

export function buildPortfolioMetadata(raw, progress, scope) {
  const records = raw || [];
  const timestamps = records
    .map((record) => isoTimestamp(record.underwritingTiming?.at || record.updatedAt || record.createdAt))
    .filter(Boolean)
    .sort();
  const metadata = {
    sourceSystem: "CrediArc saved company summaries",
    dataAsOf: timestamps.length ? timestamps[timestamps.length - 1] : null,
    snapshotGeneratedAt: new Date().toISOString(),
    uploadJob: null,
  };
  if (scope === "ar" || !progress) return metadata;

  const companyIds = Array.isArray(progress.companyIds) ? progress.companyIds : [];
  const companyIdSet = new Set(companyIds);
  const jobStartedAt = Date.parse(progress.createdAt) || 0;
  let underwritingCompleted = 0;
  let underwritingInProgress = 0;
  const completedIds = new Set();
  const activeIds = new Set();
  records.forEach((record) => {
    if (!companyIdSet.has(record.companyId)) return;
    const completedAt = Date.parse(isoTimestamp(record.underwritingTiming?.at || record.updatedAt) || "") || 0;
    if (record.underwritingInProgress === true) {
      underwritingInProgress += 1;
      activeIds.add(record.companyId);
    } else if (completedAt >= jobStartedAt) {
      underwritingCompleted += 1;
      completedIds.add(record.companyId);
    }
  });
  const underwritingTotal = companyIds.length;
  const underwritingQueued = Math.max(0, underwritingTotal - underwritingCompleted - underwritingInProgress);
  const recordById = new Map(records.map((record) => [record.companyId, record]));
  const queuedRecords = companyIds
    .filter((companyId) => !completedIds.has(companyId) && !activeIds.has(companyId))
    .map((companyId) => recordById.get(companyId))
    .filter(Boolean);
  const queuedRequestedLimits = queuedRecords.map((record) => {
    let data = record.summaryData || {};
    if (typeof data === "string") {
      try { data = JSON.parse(data || "{}"); } catch (error) { data = {}; }
    }
    const rawValue = pick(data, "Amount Requested", "Requested Limit", "Credit Limit Requested");
    return { present: rawValue !== null || record.requestedLimit !== undefined, value: num(rawValue) || num(record.requestedLimit) };
  });
  const totalCompanies = Number(progress.totalCompanies || 0);
  const processedCompanies = Number(progress.processedCompanies || 0);
  const completed = processedCompanies >= totalCompanies && underwritingTotal > 0 && underwritingCompleted >= underwritingTotal;
  metadata.uploadJob = {
    id: progress.id || null,
    filename: progress.filename || null,
    startedAt: isoTimestamp(progress.createdAt),
    status: ["failed", "cancelled"].includes(progress.status)
      ? progress.status
      : processedCompanies < totalCompanies ? "processing_companies" : completed ? "completed" : "underwriting",
    processedCompanies,
    totalCompanies,
    underwritingTotal,
    underwritingCompleted,
    underwritingInProgress,
    underwritingQueued,
    underwritingQueuedWithRequestedLimit: queuedRequestedLimits.filter((row) => row.present).length,
    underwritingQueuedMissingRequestedLimit: Math.max(0, underwritingQueued - queuedRequestedLimits.filter((row) => row.present).length),
    underwritingQueuedRequestedLimitUpperBound: queuedRequestedLimits.reduce((sum, row) => sum + row.value, 0),
  };
  return metadata;
}

export function buildPortfolioContext(rows, budget = PORTFOLIO_CONTEXT_BUDGET) {
  const compact = (rows || []).map(({ rationale, isAr, ...row }) => row);
  if (JSON.stringify(compact).length <= budget) {
    return { rows: compact, totalRows: compact.length, includedRows: compact.length, summarized: false };
  }

  const prioritized = [...compact].sort((a, b) => {
    const aGap = Math.max(0, a.amountRequested - a.recommendedLimit);
    const bGap = Math.max(0, b.amountRequested - b.recommendedLimit);
    return bGap - aGap || b.totalArBalance - a.totalArBalance || b.arcScore - a.arcScore;
  });
  const included = [];
  let size = 2;
  for (const row of prioritized) {
    const rowSize = JSON.stringify(row).length + 1;
    if (included.length && size + rowSize > budget) break;
    included.push(row);
    size += rowSize;
  }
  return { rows: included, totalRows: compact.length, includedRows: included.length, summarized: true };
}

const money = (value) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(num(value));
const moneyExact = (value) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(num(value));
const percentage = (value, total, digits = 1) => total ? `${(num(value) / num(total) * 100).toFixed(digits)}%` : "0.0%";
const riskBand = (score) => score === null ? "Missing" : score >= 8 ? "High" : score >= 4 ? "Medium" : "Low";
const artifactBlock = (artifact) => `\n\n\`\`\`aiarc\n${JSON.stringify(artifact)}\n\`\`\``;
const GENERIC_COMPANY_TERMS = new Set([
  "complete", "company", "high", "low", "medium", "missing", "unknown",
]);

export function isCompoundPortfolioQuestion(question) {
  const clean = String(question || "").toLowerCase();
  const advancedInstructions = [
    /\b(?:decompose|decomposition|synthesi[sz]e|board-ready|decision matrix|outlier review)\b/,
    /\b(?:without double-counting|avoid double-counting|upper bound|allocation gap|percentage points)\b/,
    /\b(?:average|median).{0,80}\b(?:by|within|each)\b.{0,40}\b(?:arc|risk band|sector|country)\b/,
    /\b(?:crossing|cross-tab|matrix)\b.{0,80}\b(?:arc|risk|sector|country)\b/,
    /\b(?:two-factor|multi-factor|segmented|different(?:ial|iated))\b.{0,80}\b(?:stress|scenario|downside)\b/,
    /\b(?:every company|all companies)\b.{0,80}\b(?:where|with)\b/,
    /\btop\s*\d+\b.{0,120}\b(?:then|and also|summari[sz]e|mix|within each)\b/,
    /\b(?:compare|combine|assess|evaluate|test whether|identify)\b.{0,180}\b(?:and|versus|with)\b.{0,180}\b(?:explain|quantify|calculate|identify|summari[sz]e|flag|reconcile)\b/,
  ];
  const metricFamilies = [
    /arc|risk band/,
    /recommended limit|capacity|exposure/,
    /requested limit|requested demand|shortfall/,
    /sector|industry/,
    /country/,
    /queue|underwriting status|completed cases/,
    /missing|completeness|data quality/,
    /concentration|top[- ]?\d+|median|average/,
  ].filter((pattern) => pattern.test(clean)).length;
  const deliverables = (clean.match(/\b(?:calculate|compare|quantify|identify|list|show|summari[sz]e|explain|flag|reconcile|estimate|give|cite|include)\b/g) || []).length;
  return advancedInstructions.some((pattern) => pattern.test(clean))
    || (metricFamilies >= 3 && deliverables >= 2)
    || (clean.length >= 220 && metricFamilies >= 2);
}

export function isConversationDependentQuestion(question) {
  const clean = String(question || "").toLowerCase();
  return [
    /\b(?:previous|prior|earlier|immediately previous|already established|this conversation|this chat)\b/,
    /\b(?:continue|replace|repair|recalculate|challenge|audit)\b/,
    /\breconcile\s+(?:it|this|that|back|your|the baseline|the prior|the previous)\b/,
    /\b(?:the first issue|those two|that conclusion|your conclusion|your prioritization|you just|you identified|you chose)\b/,
    /\b(?:does (?:that|this)|whether (?:that|this)|changes? the)\b/,
  ].some((pattern) => pattern.test(clean));
}

export function requiresDeepTenantCompanyContext(question) {
  const clean = String(question || "").toLowerCase();
  return [
    /\b(?:staff|personnel|employees?|people|contacts?|management|managers?|leadership|executives?|directors?|officers?|owners?|founders?)\b/,
    /\b(?:financial|alternative[- ]data|credit|underwriting|risk)\s+reports?\b/,
    /\b(?:reports?|files?|documents?|attachments?|uploads?)\b/,
    /\b(?:questions?|answers?|additional info(?:rmation)?)\b/,
    /\b(?:activity|audit|company|processing)\s+logs?\b/,
    /\b(?:aging|receivables?|invoices?|payments?)\s+(?:data|records?|reports?|schedule|details?)\b/,
    /\b(?:aggregated|raw|source|supporting|saved)\s+(?:underwriting|company|tenant)?\s*data\b/,
    /\b(?:all|full|complete|every(?:thing)?)\s+(?:available|saved|tenant|company)?\s*(?:data|record|profile|history|evidence)\b/,
    /\b(?:balance sheet|income statement|cash[- ]flow|financial statements?)\b/,
    /\b(?:debt|notes?|borrowings?|credit facilit(?:y|ies)).{0,80}\b(?:matur(?:e|es|ed|ing|ity|ities)|due|repay(?:ment)?|next\s+\d+\s+months?)\b/,
    /\b(?:10-k|10-q|sec filings?|annual reports?|quarterly reports?|debt maturit(?:y|ies))\b/,
  ].some((pattern) => pattern.test(clean));
}

export function normalizeCompanyText(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function allowsPublicCompanyResearch(question, rows) {
  const clean = normalizeCompanyText(question);
  const namesCompany = (rows || []).some((row) => {
    const company = normalizeCompanyText(row.company);
    return company.length >= 4 && clean.includes(company);
  });
  if (!namesCompany) return false;
  return [
    /\b(?:debt|notes?|borrowings?|credit facilit(?:y|ies)).{0,80}\b(?:matur|due|repay|next \d+ months?)\b/,
    /\b(?:10 k|10 q|sec filing|annual report|quarterly report|public filing)\b/,
    /\b(?:latest|recent|current)\b.{0,50}\b(?:news|filing|rating|lawsuit|litigation|bankruptcy|regulatory)\b/,
    /\b(?:public sources?|official sources?|investor relations|search the web|web search)\b/,
  ].some((pattern) => pattern.test(clean));
}

const compactMessageContent = (content, limit = 4000) => {
  const clean = String(content || "")
    .replace(/```aiarc[\s\S]*?```/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (clean.length <= limit) return clean;
  const half = Math.floor((limit - 60) / 2);
  return `${clean.slice(0, half)}\n\n…[middle omitted from transcript]…\n\n${clean.slice(-half)}`;
};

export function compactConversationMessages(messages) {
  const normalized = (Array.isArray(messages) ? messages : [])
    .filter((message) => !message.streaming && ["user", "assistant"].includes(message.role) && message.content)
    .map((message) => ({
      role: message.role,
      content: compactMessageContent(message.content),
      ...(message.evidence && { evidence: message.evidence }),
    }));
  if (normalized.length <= 24) return normalized;
  return [
    ...normalized.slice(0, 4),
    { role: "assistant", content: "Earlier middle turns were compacted for reliability; retain the opening decision context and the newest ten exchanges." },
    ...normalized.slice(-20),
  ];
}

export function buildConversationEvidence(messages) {
  return (Array.isArray(messages) ? messages : [])
    .filter((message) => !message.streaming && message.role === "assistant" && message.evidence)
    .slice(-12)
    .map((message) => message.evidence);
}

export function buildTurnSpecificAnalytics(question, portfolio) {
  const clean = String(question || "").toLowerCase();
  const rows = portfolio?.rows || [];
  const analytics = portfolio?.advancedAnalytics;
  if (!rows.length || !analytics) return null;

  const highCapMatch = clean.match(/cap every high[- ]risk recommended limit at\s*\$?\s*([\d,.]+)/);
  const mediumReductionMatch = clean.match(/reduce every medium[- ]risk recommended limit by\s*([\d.]+)\s*%/);
  if (highCapMatch && mediumReductionMatch) {
    const highCap = num(highCapMatch[1]);
    const mediumReductionPct = num(mediumReductionMatch[1]);
    const baselineByBand = Object.fromEntries(analytics.riskBandStats.map((row) => [row.riskBand, row.recommendedExposure]));
    const stressedRows = rows.map((row) => {
      const band = riskBand(row.arcScore);
      const baseline = num(row.recommendedLimit);
      const stressed = band === "High"
        ? Math.min(baseline, highCap)
        : band === "Medium" ? baseline * (1 - mediumReductionPct / 100) : baseline;
      return { band, baseline, stressed };
    });
    const stressedByBand = ["Low", "Medium", "High", "Missing"].map((band) => {
      const members = stressedRows.filter((row) => row.band === band);
      const stressedCapacity = members.reduce((sum, row) => sum + row.stressed, 0);
      return {
        riskBand: band,
        baselineCapacity: baselineByBand[band] || 0,
        capacityLoss: (baselineByBand[band] || 0) - stressedCapacity,
        stressedCapacity,
      };
    });
    const totalStressedCapacity = stressedByBand.reduce((sum, row) => sum + row.stressedCapacity, 0);
    return {
      type: "row_level_policy_stress",
      assumptions: {
        highRiskRule: `min(stored recommended limit, ${highCap}) for ARC >= 8`,
        mediumRiskRule: `stored recommended limit × ${(1 - mediumReductionPct / 100).toFixed(6)} for 4 <= ARC < 8`,
        lowAndMissingRule: "stored recommended limit unchanged",
        requestedDemand: "unchanged",
        priorScenarioTreatment: "This replacement policy supersedes the earlier scenario; reductions are not additive.",
      },
      rowsEvaluated: rows.length,
      stressedByBand: stressedByBand.map((row) => ({
        ...row,
        stressedCapacitySharePct: totalStressedCapacity ? row.stressedCapacity / totalStressedCapacity * 100 : 0,
      })),
      baselineCapacity: analytics.totalRecommendedCapacity,
      totalCapacityLoss: analytics.totalRecommendedCapacity - totalStressedCapacity,
      totalStressedCapacity,
    };
  }

  if (/remove the 15 largest|subtract \$?76,047,194\.10 from \$?298,066,117\.80|remaining shortfall/.test(clean)) {
    const removed = analytics.top15RequestedLimitGapCohort.combinedShortfall;
    const original = analytics.totalRequestedLimitShortfall;
    const remainder = original - removed;
    return {
      type: "top_15_shortfall_removal",
      originalShortfall: original,
      removedShortfall: removed,
      remainingShortfall: remainder,
      remainingShareOfOriginalPct: original ? remainder / original * 100 : 0,
      instructionConstraints: /do not reuse the original percentage/.test(clean)
        ? ["Report the remaining-share percentage precisely; do not restate the removed cohort's prior percentage."]
        : [],
      note: "This arithmetic does not by itself resolve or remove the separate missing-sector remediation priority.",
    };
  }
  return null;
}

export function buildTurnPortfolioContext(question, rows, defaultContext, turnSpecificAnalytics) {
  const clean = String(question || "").toLowerCase();
  const normalizedQuestion = normalizeCompanyText(question);
  const allRows = rows || [];
  const namedRows = allRows.filter((row) => {
    const company = normalizeCompanyText(row.company);
    return company.length >= 4 && normalizedQuestion.includes(company);
  });
  if (namedRows.length) {
    return {
      rows: namedRows,
      totalRows: allRows.length,
      includedRows: namedRows.length,
      summarized: namedRows.length !== allRows.length,
      note: `Exact stored rows for the ${namedRows.length} named compan${namedRows.length === 1 ? "y" : "ies"}; full-portfolio aggregates still cover all ${allRows.length} records.`,
    };
  }

  const analyticsOnly = turnSpecificAnalytics
    || isConversationDependentQuestion(clean)
    || /\b(?:baseline decision|board decision|committee recommendation|capacity|shortfall|arc bands?|top-10 concentration|classification blind spots?|queue status)\b/.test(clean);
  if (analyticsOnly) {
    return {
      rows: [],
      totalRows: allRows.length,
      includedRows: 0,
      summarized: true,
      note: `All ${allRows.length} records were loaded and deterministically aggregated. Raw rows were omitted from this turn because the requested figures are present in full-portfolio analytics or conversation state.`,
    };
  }
  return defaultContext;
}

const analyticsFooter = (rows, method) => [
  `**Data used** — Deterministic advanced analytics calculated from all ${rows.length.toLocaleString()} saved portfolio records.`,
  `**Method** — ${method}`,
].join("\n\n");

const mixTable = (title, rows) => [
  `### ${title}`,
  "| Group | Companies | Shortfall | Recommended exposure |",
  "|---|---:|---:|---:|",
  ...rows.map((row) => `| ${row.label} | ${row.companies.toLocaleString()} | ${moneyExact(row.shortfall)} | ${moneyExact(row.recommendedExposure)} |`),
].join("\n");

export function answerAdvancedPortfolioQuestion(question, portfolio) {
  const clean = String(question || "").toLowerCase();
  const rows = portfolio?.rows || [];
  const aggregates = portfolio?.aggregates || {};
  const analytics = portfolio?.advancedAnalytics;
  const job = portfolio?.metadata?.uploadJob;
  if (!rows.length || !analytics) return null;

  if (/risk-adjusted capacity|allocation gaps?.*percentage points|company(?:'s)? share.*capacity share/.test(clean)) {
    const bands = analytics.riskBandStats;
    const ranked = [...bands].sort((a, b) => b.allocationGapPctPoints - a.allocationGapPctPoints);
    return [
      "## Risk-adjusted capacity diagnostic",
      "| ARC band | Companies | Company share | Recommended capacity | Capacity share | Allocation gap |",
      "|---|---:|---:|---:|---:|---:|",
      ...bands.map((row) => `| ${row.riskBand} | ${row.companies.toLocaleString()} | ${row.companySharePct.toFixed(2)}% | ${moneyExact(row.recommendedExposure)} | ${row.capacitySharePct.toFixed(2)}% | ${row.allocationGapPctPoints >= 0 ? "+" : ""}${row.allocationGapPctPoints.toFixed(2)} pp |`),
      `Counts reconcile to **${analytics.totalCompanies.toLocaleString()} companies** and capacity reconciles to **${moneyExact(analytics.totalRecommendedCapacity)}**.`,
      `The largest positive allocation gap is **${ranked[0].riskBand} at +${ranked[0].allocationGapPctPoints.toFixed(2)} percentage points**. The largest negative gap is **${ranked[ranked.length - 1].riskBand} at ${ranked[ranked.length - 1].allocationGapPctPoints.toFixed(2)} percentage points**.`,
      `This indicates strong underwriting selectivity: capacity is deliberately shifted toward lower-risk companies while the High band receives only ${bands.find((row) => row.riskBand === "High").capacitySharePct.toFixed(2)}% of capacity despite representing ${bands.find((row) => row.riskBand === "High").companySharePct.toFixed(2)}% of companies.`,
      analyticsFooter(rows, "For each canonical ARC band, divided company count by full portfolio count and recommended exposure by full recommended capacity. Allocation gap = capacity share − company share."),
    ].join("\n\n");
  }

  if (/decompose.*shortfall|shortfall.*high-risk.*low\/?medium|shortfall.*low\/?medium.*high-risk/.test(clean)) {
    const high = analytics.riskBandStats.find((row) => row.riskBand === "High");
    const lowMedium = analytics.riskBandStats.filter((row) => ["Low", "Medium"].includes(row.riskBand));
    const lowMediumSummary = {
      companies: lowMedium.reduce((sum, row) => sum + row.companies, 0),
      restrictedCompanies: lowMedium.reduce((sum, row) => sum + row.restrictedCompanies, 0),
      shortfall: lowMedium.reduce((sum, row) => sum + row.requestedLimitShortfall, 0),
    };
    const lowMediumAverage = lowMediumSummary.restrictedCompanies ? lowMediumSummary.shortfall / lowMediumSummary.restrictedCompanies : null;
    return [
      "## Requested-limit shortfall decomposition",
      "| Risk group | Companies | Restricted companies | Shortfall | Average per restricted company | Share of total shortfall |",
      "|---|---:|---:|---:|---:|---:|",
      `| High (ARC ≥8) | ${high.companies.toLocaleString()} | ${high.restrictedCompanies.toLocaleString()} | ${moneyExact(high.requestedLimitShortfall)} | ${high.averageShortfallPerRestrictedCompany === null ? "Unavailable" : moneyExact(high.averageShortfallPerRestrictedCompany)} | ${high.shortfallSharePct.toFixed(2)}% |`,
      `| Low + Medium (ARC <8) | ${lowMediumSummary.companies.toLocaleString()} | ${lowMediumSummary.restrictedCompanies.toLocaleString()} | ${moneyExact(lowMediumSummary.shortfall)} | ${lowMediumAverage === null ? "Unavailable" : moneyExact(lowMediumAverage)} | ${percentage(lowMediumSummary.shortfall, analytics.totalRequestedLimitShortfall, 2)} |`,
      `The two groups reconcile to **${moneyExact(analytics.totalRequestedLimitShortfall)}**. Companies with missing ARC scores contribute **${moneyExact(analytics.riskBandStats.find((row) => row.riskBand === "Missing").requestedLimitShortfall)}** and are disclosed separately rather than silently assigned to a band.`,
      analyticsFooter(rows, "Filtered companies by canonical ARC bands; restricted means requested amount > 0 and recommended limit < requested amount. Shortfall = requested − recommended."),
    ].join("\n\n");
  }

  if (/concentration blind spots?|fully classified across all three|missing sector.*missing arc.*missing countr/.test(clean)) {
    const blind = analytics.classificationBlindSpots;
    return [
      "## Portfolio classification blind spots",
      "| Missing dimension | Companies | Recommended exposure | Share of total capacity |",
      "|---|---:|---:|---:|",
      ...blind.dimensions.map((row) => `| ${row.field} | ${row.companiesMissing.toLocaleString()} | ${moneyExact(row.recommendedExposure)} | ${row.capacitySharePct.toFixed(2)}% |`),
      `Across the union of all three dimensions, **${blind.incompleteAnyOfSectorArcCountry.companies.toLocaleString()} companies** representing **${moneyExact(blind.incompleteAnyOfSectorArcCountry.recommendedExposure)}**, or **${blind.incompleteAnyOfSectorArcCountry.capacitySharePct.toFixed(2)}% of total recommended capacity**, cannot be fully classified.`,
      `The complementary fully classified population is **${blind.completeAcrossSectorArcCountry.companies.toLocaleString()} companies** with **${moneyExact(blind.completeAcrossSectorArcCountry.recommendedExposure)}** of capacity.`,
      `**No-double-counting control** — ${blind.noDoubleCountingMethod}`,
      analyticsFooter(rows, "Measured missingness separately by field, then calculated one company-level union for sector OR ARC score OR country and summed each affected company's recommended limit once."),
    ].join("\n\n");
  }

  if (/two-factor downside|25%.*(?:arc|high).*(?:10%|medium)|10%.*(?:medium).*(?:25%|high)/.test(clean)) {
    const stress = analytics.differentiatedRiskStress;
    return [
      "## Differentiated ARC-band downside scenario",
      `Baseline recommended capacity is **${moneyExact(stress.baselineCapacity)}**. The scenario removes **${moneyExact(stress.capacityLoss)}**, leaving **${moneyExact(stress.stressedCapacity)}** of stressed capacity.`,
      "| ARC band | Stressed recommended capacity | Post-stress capacity share |",
      "|---|---:|---:|",
      ...stress.stressedBandResults.map((row) => `| ${row.riskBand} | ${moneyExact(row.stressedRecommendedExposure)} | ${row.stressedCapacitySharePct.toFixed(2)}% |`),
      `Requested demand remains unchanged at **${moneyExact(aggregates.totalRequestedLimit)}**.`,
      `**Assumptions** — ${stress.assumptions.join("; ")}.`,
      analyticsFooter(rows, "Applied the stated reduction to each band's existing recommended exposure, summed the dollar loss, and divided each stressed band exposure by total stressed capacity."),
    ].join("\n\n");
  }

  if (/(?:15|fifteen).{0,30}(?:largest|top).{0,50}(?:requested-minus-recommended|limit gaps?|shortfall)|(?:top|largest).{0,20}(?:15|fifteen).{0,80}(?:gap|shortfall)|largest requested-minus-recommended limit gaps/.test(clean)) {
    const cohort = analytics.top15RequestedLimitGapCohort;
    return [
      "## 15 largest requested-limit gaps",
      "| Rank | Company | Gap | Requested | Recommended | ARC band | Sector | Country |",
      "|---:|---|---:|---:|---:|---|---|---|",
      ...cohort.records.map((row, index) => `| ${index + 1} | ${row.company} | ${moneyExact(row.requestedLimitGap)} | ${moneyExact(row.amountRequested)} | ${moneyExact(row.recommendedLimit)} | ${row.riskBand} | ${row.sector} | ${row.country} |`),
      `Combined shortfall: **${moneyExact(cohort.combinedShortfall)}**, equal to **${cohort.shareOfPortfolioShortfallPct.toFixed(2)}%** of the portfolio's ${moneyExact(analytics.totalRequestedLimitShortfall)} total shortfall.`,
      mixTable("ARC-band mix", cohort.riskBandMix),
      mixTable("Sector mix", cohort.sectorMix),
      mixTable("Country mix", cohort.countryMix),
      `**Repeated data-quality pattern** — ${cohort.records.filter((row) => ["Missing / unavailable", "Unknown"].includes(row.sector)).length} of the 15 records have an unavailable or non-informative sector classification; ${cohort.records.filter((row) => row.country === "Missing / unavailable").length} have no country.`,
      analyticsFooter(rows, "Calculated positive requested-minus-recommended gaps for every company, sorted descending, selected exactly 15, and grouped that cohort without inferring missing classifications."),
    ].join("\n\n");
  }

  if (/median recommended limit by arc band|average and median recommended limit.*(?:arc|risk) band|top-10 concentration within each band/.test(clean)) {
    const low = analytics.riskBandStats.find((row) => row.riskBand === "Low");
    const high = analytics.riskBandStats.find((row) => row.riskBand === "High");
    return [
      "## Recommended-limit distribution within ARC bands",
      "| ARC band | Companies | Average recommended limit | Median recommended limit | Top-10 exposure within band | Top-10 share within band |",
      "|---|---:|---:|---:|---:|---:|",
      ...analytics.riskBandStats.map((row) => `| ${row.riskBand} | ${row.companies.toLocaleString()} | ${row.averageRecommendedLimit === null ? "Unavailable" : moneyExact(row.averageRecommendedLimit)} | ${row.medianRecommendedLimit === null ? "Unavailable" : moneyExact(row.medianRecommendedLimit)} | ${moneyExact(row.top10RecommendedExposure)} | ${row.top10ShareWithinBandPct.toFixed(2)}% |`),
      `The evidence supports broad low-risk allocation rather than capacity being merely diluted by many small names: Low Risk carries **${moneyExact(low.recommendedExposure)}** with a **${moneyExact(low.medianRecommendedLimit)} median**, while its ten largest names are only **${low.top10ShareWithinBandPct.toFixed(2)}%** of Low-Risk capacity. High Risk is the opposite—its top ten names hold **${high.top10ShareWithinBandPct.toFixed(2)}%** of that band—but the entire High band is only **${high.capacitySharePct.toFixed(2)}%** of portfolio capacity. Average above median in Low and Medium confirms moderate right skew, not dominant single-name concentration.`,
      analyticsFooter(rows, "Calculated mean and true median from every company's stored recommended limit inside each canonical ARC band; sorted each band independently for its top-10 concentration."),
    ].join("\n\n");
  }

  if (/decision matrix.*sector.*(?:arc|risk band)|crossing sector completeness with arc/.test(clean)) {
    const matrix = analytics.sectorCompletenessRiskMatrix;
    const priority = [...matrix].sort((a, b) => b.requestedLimitShortfall - a.requestedLimitShortfall || b.recommendedExposure - a.recommendedExposure)[0];
    return [
      "## Sector completeness × ARC risk-band decision matrix",
      "| Sector status | ARC band | Companies | Recommended exposure | Requested-limit shortfall |",
      "|---|---|---:|---:|---:|",
      ...matrix.map((row) => `| ${row.sectorCompleteness} | ${row.riskBand} | ${row.companies.toLocaleString()} | ${moneyExact(row.recommendedExposure)} | ${moneyExact(row.requestedLimitShortfall)} |`),
      `Credit operations should prioritize **${priority.sectorCompleteness} / ${priority.riskBand}**: it contains **${priority.companies.toLocaleString()} companies**, **${moneyExact(priority.recommendedExposure)}** of recommended exposure, and the matrix's largest shortfall at **${moneyExact(priority.requestedLimitShortfall)}**.`,
      analyticsFooter(rows, "Crossed explicit sector presence/missingness with canonical ARC band; counted companies and summed recommended limits and positive requested-limit gaps once in each mutually exclusive cell."),
    ].join("\n\n");
  }

  if (/underwriting queue.*potential materiality|queued cases.*upper bound|queue.*upper bound.*requested/.test(clean)) {
    if (!job) {
      return [
        "## Underwriting queue materiality",
        "No explicit upload-job queue metadata is available, so queued-case count and requested-limit upper bound cannot be calculated reliably.",
        analyticsFooter(rows, "Checked the active portfolio metadata and did not infer queue status from company fields."),
      ].join("\n\n");
    }
    const completedPct = job.underwritingTotal ? job.underwritingCompleted / job.underwritingTotal * 100 : 0;
    const queuedPct = job.underwritingTotal ? job.underwritingQueued / job.underwritingTotal * 100 : 0;
    const hasCompleteQueueDemand = job.underwritingQueuedMissingRequestedLimit === 0;
    const materialityStatement = hasCompleteQueueDemand
      ? `The queued cases have **${moneyExact(job.underwritingQueuedRequestedLimitUpperBound)}** of requested limits. Treating every queued request as fully approved gives a conservative exposure upper bound; actual recommended capacity should be lower or equal after underwriting.`
      : `The identifiable requested demand is **${moneyExact(job.underwritingQueuedRequestedLimitUpperBound)}** across **${job.underwritingQueuedWithRequestedLimit.toLocaleString()}** queued cases. This is **not a portfolio-impact upper bound** because **${job.underwritingQueuedMissingRequestedLimit.toLocaleString()} queued cases lack requested limits**. No defensible finite upper bound can be calculated from the saved data until those requests are populated.`;
    return [
      "## Underwriting queue materiality",
      `Of **${job.underwritingTotal.toLocaleString()}** underwriting cases, **${job.underwritingCompleted.toLocaleString()} (${completedPct.toFixed(2)}%)** are complete, **${job.underwritingInProgress.toLocaleString()}** are active, and **${job.underwritingQueued.toLocaleString()} (${queuedPct.toFixed(2)}%)** are queued.`,
      materialityStatement,
      `**Known:** ${job.underwritingCompleted.toLocaleString()} complete, ${job.underwritingQueued.toLocaleString()} queued, and ${job.underwritingQueuedWithRequestedLimit.toLocaleString()} queued requested limits available. **Assumption:** a requested limit is a maximum potential demand measure, not an approval forecast. **Unavailable:** requested demand for ${job.underwritingQueuedMissingRequestedLimit.toLocaleString()} queued cases and their future recommended limits.`,
      analyticsFooter(rows, "Matched active upload company IDs to saved records, separated completed/active/queued status using stored workflow timestamps, and summed requested limits only for identifiable queued records."),
    ].join("\n\n");
  }

  if (/outlier review|recommended limit is at least \$?1 million.*arc.*8|recommended limit.*zero.*requested limit.*\$?5 million/.test(clean)) {
    const outliers = analytics.outlierReviewAtSpecifiedThresholds;
    return [
      "## Limit-and-risk outlier review",
      `**${outliers.companies.toLocaleString()} companies** meet at least one specified rule.`,
      "| Company | Requested limit | Recommended limit | ARC score | Risk band | Matched rule(s) |",
      "|---|---:|---:|---:|---|---|",
      ...outliers.records.map((row) => `| ${row.company} | ${moneyExact(row.amountRequested)} | ${moneyExact(row.recommendedLimit)} | ${row.arcScore === null ? "Unavailable" : row.arcScore.toFixed(1)} | ${row.riskBand} | ${row.matchedRules.join("; ")} |`),
      `Combined recommended exposure: **${moneyExact(outliers.totalRecommendedExposure)}**. Combined requested demand: **${moneyExact(outliers.totalRequestedDemand)}**.`,
      analyticsFooter(rows, "Applied both predicates to every row, used an OR union so companies meeting both rules appear once, and summed the resulting cohort."),
    ].join("\n\n");
  }

  if (/board-ready portfolio memo|board.*memo.*capacity.*shortfall|memo synthesizing capacity/.test(clean)) {
    const high = analytics.riskBandStats.find((row) => row.riskBand === "High");
    const missingSector = analytics.classificationBlindSpots.dimensions.find((row) => row.field === "Sector / industry");
    const topSector = analytics.sectorConcentrations.find((row) => row.sector !== "Missing / unavailable");
    const top10 = analytics.top10PortfolioConcentration;
    const queueText = job
      ? `${job.underwritingCompleted.toLocaleString()} complete and ${job.underwritingQueued.toLocaleString()} queued of ${job.underwritingTotal.toLocaleString()}`
      : "queue metadata unavailable";
    return [
      "## Board portfolio memo",
      `The portfolio contains **${analytics.totalCompanies.toLocaleString()} prospects**, **${moneyExact(analytics.totalRecommendedCapacity)}** of recommended capacity, and **${moneyExact(analytics.totalRequestedLimitShortfall)}** of requested-limit shortfall. High Risk represents **${high.companies.toLocaleString()} companies** but only **${moneyExact(high.recommendedExposure)} (${high.capacitySharePct.toFixed(2)}%)** of capacity. The top ten exposures total **${moneyExact(top10.recommendedExposure)} (${top10.capacitySharePct.toFixed(2)}%)**, indicating limited single-name concentration at portfolio level. Sector coverage remains the larger blind spot: **${missingSector.companiesMissing.toLocaleString()} companies** and **${moneyExact(missingSector.recommendedExposure)}** lack sector classification. Among classified sectors, the largest is **${topSector.sector}** at **${moneyExact(topSector.recommendedExposure)} (${topSector.capacitySharePct.toFixed(2)}%)**. Underwriting status is ${queueText}.`,
      "### Priority actions",
      `1. **Close the sector-data blind spot.** Trigger escalation while more than **10% of capacity** lacks sector classification; the current level is **${missingSector.capacitySharePct.toFixed(2)}%**, representing **${missingSector.companiesMissing.toLocaleString()} companies** and **${moneyExact(missingSector.recommendedExposure)}**.`,
      `2. **Maintain the High-Risk capacity gate.** Require committee review if High-Risk capacity exceeds **1% of portfolio capacity** or any High-Risk recommended limit reaches **$1 million**; currently High Risk is **${high.capacitySharePct.toFixed(2)}%** across **${high.companies.toLocaleString()} companies**, with **${moneyExact(high.requestedLimitShortfall)}** of shortfall.`,
      `3. **Clear the underwriting queue before portfolio sign-off.** Require queued cases below **1% of the underwriting population** and zero cases missing requested-limit data; current status is ${job ? `**${job.underwritingQueued.toLocaleString()} queued (${(job.underwritingTotal ? job.underwritingQueued / job.underwritingTotal * 100 : 0).toFixed(2)}%)**, with requested limits unavailable for **${job.underwritingQueuedMissingRequestedLimit.toLocaleString()} queued cases** and therefore no defensible finite exposure upper bound` : "**unavailable**"}.`,
      analyticsFooter(rows, "Synthesized reconciled full-portfolio capacity, shortfall, ARC bands, top-10 exposure concentration, sector concentration/missingness, and explicit upload-job status. Thresholds are governance triggers, not forecasts."),
    ].join("\n\n");
  }

  return null;
}

const requestedResultCount = (question, fallback = 5) => {
  const clean = String(question || "").toLowerCase();
  const wordCounts = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
  const numeric = clean.match(/\b(?:top|list|show|find|give me)\s+(?:the\s+)?(\d{1,3})\b/);
  if (numeric) return Math.min(200, Math.max(1, Number(numeric[1])));
  const word = Object.entries(wordCounts).find(([label]) => new RegExp(`\\b${label}\\b`).test(clean));
  return word ? word[1] : fallback;
};

const rankingTable = (rows, includeGap = false) => [
  `| Company | ARC score | Requested limit | Recommended limit |${includeGap ? " Gap |" : ""}`,
  `|---|---:|---:|---:|${includeGap ? "---:|" : ""}`,
  ...rows.map((row) => `| ${row.company} | ${num(row.arcScore).toFixed(1)} | ${money(row.amountRequested)} | ${money(row.recommendedLimit)} |${includeGap ? ` ${money(row.amountRequested - row.recommendedLimit)} |` : ""}`),
].join("\n");

export function answerPortfolioQuestion(question, portfolio, scope) {
  const clean = String(question || "").toLowerCase();
  const rows = portfolio?.rows || [];
  const aggregates = portfolio?.aggregates || {};
  if (!rows.length) return null;

  if (/other (?:crediarc )?(?:tenant|customer|account)|every other crediarc|cross[- ]tenant/.test(clean)) {
    return "I cannot access or disclose another CrediArc customer's data. ArcAI is restricted to the active tenant portfolio.";
  }

  // Follow-ups must be interpreted with the transcript. A familiar keyword
  // must never replace the user's requested recalculation, correction, or audit
  // with an unrelated canned answer.
  if (isConversationDependentQuestion(clean)) return null;

  // The compact portfolio snapshot intentionally contains only the fields
  // needed for deterministic portfolio math. Questions about richer company
  // evidence must reach ArcAI's tenant-scoped backend context instead of being
  // answered from that compact snapshot.
  if (requiresDeepTenantCompanyContext(clean)) return null;

  if (/exact total recommended limit|total recommended limit.*how many|how many companies.*total recommended limit/.test(clean)) {
    return [
      "## Full-portfolio recommended capacity",
      `The exact total recommended limit is **${moneyExact(aggregates.recommendedLimit)}** across **${rows.length.toLocaleString()} companies**.`,
      `**Data used** — All ${rows.length.toLocaleString()} saved portfolio records.`,
      "**Method** — Summed each record's recommended limit once; no row sampling was used.",
    ].join("\n\n");
  }

  const advancedAnswer = answerAdvancedPortfolioQuestion(clean, portfolio);
  if (advancedAnswer) return advancedAnswer;

  // Predefined answers are deliberately limited to simple, single-intent
  // questions. Compound credit analysis goes to the reasoning model together
  // with exact, full-portfolio analytics calculated below.
  if (isCompoundPortfolioQuestion(clean)) return null;

  const asksForNamedCompany = /\b(?:about|company|counterparty|details?|find|for|record|show)\b/.test(clean);
  const companyMatch = rows
    .filter((row) => {
      const company = String(row.company || "").trim();
      const normalized = normalizeCompanyText(company);
      if (!asksForNamedCompany || company.length < 4 || GENERIC_COMPANY_TERMS.has(normalized)) return false;
      return normalizeCompanyText(clean).includes(normalized);
    })
    .sort((a, b) => b.company.length - a.company.length)[0];
  if (companyMatch) {
    return [
      `## ${companyMatch.company}`,
      `- **Amount requested:** ${moneyExact(companyMatch.amountRequested)}`,
      `- **Recommended limit:** ${moneyExact(companyMatch.recommendedLimit)}`,
      `- **ARC score:** ${companyMatch.arcScore === null ? "Unavailable" : `${companyMatch.arcScore.toFixed(1)} / 10`}`,
      `- **Risk band:** ${riskBand(companyMatch.arcScore)}${companyMatch.arcScore === null ? "" : " (Low <4; Medium 4–<8; High ≥8)"}`,
      `- **Country:** ${companyMatch.country || "Unavailable"}`,
      `- **Sector / industry:** ${companyMatch.sector || "Unavailable"}`,
      `- **Underwriting reason:** ${companyMatch.rationale || "Unavailable in the saved record"}`,
      `- **Record last updated:** ${companyMatch.lastUpdatedAt || "Unavailable in the saved record"}`,
      `**Data used** — Exact company-name match against all ${rows.length.toLocaleString()} saved portfolio records.`,
      "**Method** — Read the named company's stored fields directly; unavailable fields were not inferred.",
    ].join("\n\n");
  }

  const asksForTopExposure = !/(?:industry|sector)/.test(clean)
    && /(?:top|largest|biggest).{0,45}(?:exposure|recommended limit|capacity)|(?:exposure|recommended limit).{0,45}(?:top|largest|biggest)/.test(clean);
  if (asksForTopExposure) {
    const count = requestedResultCount(clean, 10);
    const exposureKey = scope === "ar" ? "totalArBalance" : "recommendedLimit";
    const exposureLabel = scope === "ar" ? "Outstanding AR" : "Recommended limit";
    const ranked = [...rows].sort((a, b) => num(b[exposureKey]) - num(a[exposureKey]) || a.company.localeCompare(b.company)).slice(0, count);
    const portfolioTotal = rows.reduce((sum, row) => sum + num(row[exposureKey]), 0);
    const rankedTotal = ranked.reduce((sum, row) => sum + num(row[exposureKey]), 0);
    const table = [
      `| Rank | Company | ${exposureLabel} | % of portfolio |`,
      "|---:|---|---:|---:|",
      ...ranked.map((row, index) => `| ${index + 1} | ${row.company} | ${moneyExact(row[exposureKey])} | ${percentage(row[exposureKey], portfolioTotal, 2)} |`),
    ].join("\n");
    return [
      `## Top ${ranked.length} portfolio exposures`,
      table,
      `The top ${ranked.length} total is **${moneyExact(rankedTotal)}**, equal to **${percentage(rankedTotal, portfolioTotal, 2)}** of the full **${moneyExact(portfolioTotal)}** portfolio exposure.`,
      `**Data used** — All ${rows.length.toLocaleString()} saved portfolio records; no 508-row subset was used.`,
      `**Method** — Sorted ${exposureLabel.toLowerCase()} descending, returned exactly ${ranked.length} companies, and divided their total by the full-portfolio total.`,
    ].join("\n\n") + artifactBlock({
      type: "chart", chartType: "bar", title: `Top ${ranked.length} portfolio exposures`,
      xKey: "company", yKeys: ["exposure"], methodology: `All ${rows.length} records; ${exposureLabel} descending.`,
      data: ranked.map((row) => ({ company: row.company, exposure: num(row[exposureKey]) })),
    });
  }

  if (/pivot/.test(clean) && /country/.test(clean) && /(?:arc|risk band)/.test(clean)) {
    const exposureKey = scope === "ar" ? "totalArBalance" : "recommendedLimit";
    const groups = new Map();
    rows.forEach((row) => {
      const country = row.country || "Missing / unavailable";
      const band = riskBand(row.arcScore);
      const key = `${country}\u0000${band}`;
      const current = groups.get(key) || { country, riskBand: band, companies: 0, exposure: 0 };
      current.companies += 1;
      current.exposure += num(row[exposureKey]);
      groups.set(key, current);
    });
    const data = [...groups.values()].sort((a, b) => b.exposure - a.exposure || a.country.localeCompare(b.country) || a.riskBand.localeCompare(b.riskBand));
    return [
      "## Country × ARC risk-band pivot",
      `The pivot contains **${data.length.toLocaleString()} country/band combinations**, reconciles to **${rows.length.toLocaleString()} companies**, and totals **${moneyExact(data.reduce((sum, row) => sum + row.exposure, 0))}** of exposure.`,
      `**Data used** — All ${rows.length.toLocaleString()} saved portfolio records; country, ARC score, and ${scope === "ar" ? "outstanding AR" : "recommended limit"}.`,
      "**Method** — Grouped missing countries explicitly, applied canonical CrediArc bands, and summed company count and exposure once per group.",
    ].join("\n\n") + artifactBlock({
      type: "pivot", title: "Country × ARC risk band", methodology: `All ${rows.length} portfolio records.`, data,
    });
  }

  if (/data quality|data completeness|missing counts?|coverage percentages?/.test(clean)) {
    const fields = [
      ["Requested limit", rows.filter((row) => !row.hasRequestedLimit).length],
      ["Recommended limit", rows.filter((row) => !row.hasRecommendedLimit).length],
      ["ARC score", rows.filter((row) => row.arcScore === null).length],
      ["Country", rows.filter((row) => !row.country).length],
      ["Sector / industry", rows.filter((row) => !row.sector).length],
      ["Last-updated timestamp", rows.filter((row) => !row.lastUpdatedAt).length],
    ];
    return [
      "## Portfolio data quality",
      "| Field | Missing | Present | Coverage |",
      "|---|---:|---:|---:|",
      ...fields.map(([label, missing]) => `| ${label} | ${missing.toLocaleString()} | ${(rows.length - missing).toLocaleString()} | ${percentage(rows.length - missing, rows.length)} |`),
      `**Scope** — Calculated from all ${rows.length.toLocaleString()} saved portfolio records. These are full-portfolio field counts, not statistics from the detailed model subset.`,
      "**Method** — Counted blank/null source fields as missing. A stored zero is present data, not a missing value.",
    ].join("\n");
  }

  if (/as-of|as of|\bfresh\b|freshness|latest stored record|source system|underwriting.*(?:complete|queued)|still queued|queue status|record timestamp/.test(clean)) {
    const metadata = portfolio?.metadata || {};
    const job = metadata.uploadJob;
    const jobLines = job ? [
      `- **Latest upload:** ${job.filename || "Filename unavailable"}`,
      `- **Upload started:** ${job.startedAt || "Unavailable"}`,
      `- **Company rows processed:** ${job.processedCompanies.toLocaleString()} of ${job.totalCompanies.toLocaleString()}`,
      `- **Underwriting:** ${job.underwritingCompleted.toLocaleString()} complete, ${job.underwritingInProgress.toLocaleString()} active, ${job.underwritingQueued.toLocaleString()} queued of ${job.underwritingTotal.toLocaleString()}`,
      `- **Workflow status:** ${job.status}`,
    ] : ["- **Upload / underwriting workflow:** No explicit active-job metadata was available."];
    return [
      "## Data freshness and processing status",
      `- **Source system:** ${metadata.sourceSystem || "Unavailable"}`,
      `- **Latest stored record update:** ${metadata.dataAsOf || "Unavailable"}`,
      `- **ArcAI snapshot generated:** ${metadata.snapshotGeneratedAt || "Unavailable"} (this is not presented as the source-data refresh time)`,
      ...jobLines,
      "**Method** — Reported stored record timestamps and upload-job status separately; no source system or completion state was inferred.",
    ].join("\n");
  }

  if (/(?:industry|sector)/.test(clean) && /(?:concentration|top|group|breakdown|largest|biggest|exposure)/.test(clean)) {
    const exposureKey = scope === "ar" ? "totalArBalance" : "recommendedLimit";
    const groups = new Map();
    rows.forEach((row) => {
      const label = row.sector || "Missing / unavailable";
      const current = groups.get(label) || { sector: label, companies: 0, exposure: 0 };
      current.companies += 1;
      current.exposure += num(row[exposureKey]);
      groups.set(label, current);
    });
    const portfolioTotal = rows.reduce((sum, row) => sum + num(row[exposureKey]), 0);
    const count = requestedResultCount(clean, 5);
    const ranked = [...groups.values()].sort((a, b) => b.exposure - a.exposure).slice(0, count);
    return [
      `## Top ${ranked.length} sector concentrations`,
      "| Sector / industry | Companies | Exposure | % of portfolio |",
      "|---|---:|---:|---:|",
      ...ranked.map((row) => `| ${row.sector} | ${row.companies.toLocaleString()} | ${moneyExact(row.exposure)} | ${percentage(row.exposure, portfolioTotal, 2)} |`),
      `Full portfolio: **${rows.length.toLocaleString()} companies** and **${moneyExact(portfolioTotal)}** exposure. Missing sectors are grouped explicitly, never inferred from company names.`,
      `**Data used** — All ${rows.length.toLocaleString()} saved portfolio records.`,
    ].join("\n");
  }

  if (/1\s*[–—-]\s*3/.test(clean) && /4\s*[–—-]\s*7/.test(clean) && /8\s*[–—-]\s*10/.test(clean)) {
    const counts = rows.reduce((out, row) => {
      if (row.arcScore === null) out.missing += 1;
      else if (row.arcScore >= 1 && row.arcScore < 4) out.oneToThree += 1;
      else if (row.arcScore >= 4 && row.arcScore < 8) out.fourToSeven += 1;
      else if (row.arcScore >= 8 && row.arcScore <= 10) out.eightToTen += 1;
      else out.outside += 1;
      return out;
    }, { oneToThree: 0, fourToSeven: 0, eightToTen: 0, missing: 0, outside: 0 });
    const reconciled = counts.oneToThree + counts.fourToSeven + counts.eightToTen + counts.missing + counts.outside;
    return [
      "## Custom ARC score buckets",
      `- **ARC 1–3 (1 ≤ ARC < 4):** ${counts.oneToThree.toLocaleString()}`,
      `- **ARC 4–7 (4 ≤ ARC < 8):** ${counts.fourToSeven.toLocaleString()}`,
      `- **ARC 8–10 (8 ≤ ARC ≤ 10):** ${counts.eightToTen.toLocaleString()}`,
      `- **Missing ARC score:** ${counts.missing.toLocaleString()}`,
      counts.outside ? `- **Outside the valid 1–10 range:** ${counts.outside.toLocaleString()}` : "",
      `The counts reconcile to **${reconciled.toLocaleString()} of ${rows.length.toLocaleString()}** portfolio companies. These are the custom buckets requested; CrediArc's canonical bands remain Low <4, Medium 4–<8, and High ≥8.`,
      `**Data used** — All ${rows.length.toLocaleString()} portfolio records; arcScore.`,
      "**Method** — Counted each valid ARC score once in the requested inclusive ranges and counted missing scores separately.",
    ].filter(Boolean).join("\n\n");
  }

  const asksForGapRanking = /requested.*(?:greater|higher|above).*recommended|requested.*recommended.*gap|largest.*shortfall/.test(clean)
    && /arc(?: score)?[^\n]{0,20}(?:8|high)|high[- ]risk/.test(clean);
  if (asksForGapRanking) {
    const count = requestedResultCount(clean);
    const matched = rows
      .filter((row) => row.arcScore !== null && row.arcScore >= 8 && row.amountRequested > row.recommendedLimit)
      .sort((a, b) => b.amountRequested - a.amountRequested || b.arcScore - a.arcScore)
      .slice(0, count);
    return [
      `## ${matched.length} largest high-risk requested-limit gaps`,
      rankingTable(matched, true),
      matched.length < count ? `Only ${matched.length} matching companies were available.` : "",
      `**Data used** — All ${rows.length.toLocaleString()} portfolio records; company, arcScore, amountRequested, and recommendedLimit.`,
      `**Method** — Filtered ARC ≥8 and requested limit > recommended limit, sorted by requested limit descending, and returned exactly ${matched.length} result${matched.length === 1 ? "" : "s"}. Gap = requested − recommended.`,
    ].filter(Boolean).join("\n\n");
  }

  if (/highest[- ]risk|riskiest/.test(clean)) {
    const count = requestedResultCount(clean);
    const ranked = rows
      .filter((row) => row.arcScore !== null)
      .sort((a, b) => b.arcScore - a.arcScore || b.amountRequested - a.amountRequested || a.company.localeCompare(b.company))
      .slice(0, count);
    return [
      `## ${ranked.length} highest-risk companies`,
      rankingTable(ranked),
      ranked.length < count ? `Only ${ranked.length} scored companies were available.` : "",
      `**Data used** — All ${rows.length.toLocaleString()} portfolio records; company, arcScore, amountRequested, and recommendedLimit.`,
      `**Method** — Sorted ARC score descending; ties were ordered by requested limit descending, then company name. Returned exactly ${ranked.length} result${ranked.length === 1 ? "" : "s"}.`,
    ].filter(Boolean).join("\n\n");
  }

  if (/what should i focus on|what should we focus on|focus on today|priorit(?:y|ies|ize).*today/.test(clean)) {
    const highRisk = rows.filter((row) => row.arcScore !== null && row.arcScore >= 8);
    const highRiskShortfall = highRisk.reduce((total, row) => total + Math.max(0, row.amountRequested - row.recommendedLimit), 0);
    const top = [...highRisk].sort((a, b) => (b.amountRequested - b.recommendedLimit) - (a.amountRequested - a.recommendedLimit)).slice(0, 5);
    return [
      "## Today's portfolio priorities",
      `1. **Review high-risk requests:** ${highRisk.length.toLocaleString()} companies are in CrediArc's High band (ARC ≥8), with ${money(highRiskShortfall)} of requested-limit shortfall within that band.`,
      `2. **Work the overall approval gap separately:** the full portfolio shortfall is ${money(aggregates.requestedLimitShortfall)} across ${(aggregates.companiesBelowRequestedLimit || 0).toLocaleString()} companies with recommended limits below requested amounts. This is a whole-portfolio figure, not a High-Risk-only count.`,
      `3. **Start with the largest High-Risk gaps:**`,
      rankingTable(top, true),
      `**Data used** — All ${rows.length.toLocaleString()} portfolio records; ARC score, requested limit, and recommended limit.`,
      "**Method** — Kept canonical High Risk at ARC ≥8, calculated High-Risk shortfall only from that subset, and reported the whole-portfolio shortfall separately.",
    ].join("\n\n");
  }

  if (!/stress test/.test(clean) && /recommended limit.*below|below.*requested|not (getting|receiving).*limit|limit.*shortfall/.test(clean)) {
    const compared = aggregates.companiesWithRequestedLimit || 0;
    const below = aggregates.companiesBelowRequestedLimit || 0;
    return [
      "## Requested-limit coverage",
      `**${below.toLocaleString()} of ${compared.toLocaleString()} companies with a requested amount have a recommended limit below that amount.**`,
      `That is **${percentage(below, compared)}** of the ${compared.toLocaleString()} companies with usable positive request data.`,
      `The total shortfall is **${money(aggregates.requestedLimitShortfall)}**.`,
      rows.length > compared ? `${(rows.length - compared).toLocaleString()} companies have no positive requested amount and are excluded from this comparison.` : "",
      `**Data used** — All ${rows.length.toLocaleString()} ${scope === "ar" ? "AR customer" : "prospect"} records; amount requested and recommended limit.`,
      "**Method** — Count where requested amount > 0 and recommended limit < requested amount; shortfall is the sum of requested amount − recommended limit.",
    ].filter(Boolean).join("\n\n");
  }

  if (/average arc|risk bands?|low.*medium.*high|high.*medium.*low/.test(clean)) {
    const bands = aggregates.riskBands || { low: 0, medium: 0, high: 0, missing: 0 };
    const exposureKey = scope === "ar" ? "totalArBalance" : "recommendedLimit";
    const bandExposure = rows.reduce((out, row) => {
      out[riskBand(row.arcScore).toLowerCase()] += num(row[exposureKey]);
      return out;
    }, { low: 0, medium: 0, high: 0, missing: 0 });
    const totalExposure = Object.values(bandExposure).reduce((sum, value) => sum + value, 0);
    return [
      "## CrediArc ARC distribution",
      `The average ARC score is **${num(aggregates.averageArc).toFixed(2)} / 10** across ${(aggregates.companiesWithArcScore || 0).toLocaleString()} scored companies.`,
      "| Band | Companies | Exposure | % of full capacity |",
      "|---|---:|---:|---:|",
      `| Low (ARC <4) | ${(bands.low || 0).toLocaleString()} | ${moneyExact(bandExposure.low)} | ${percentage(bandExposure.low, totalExposure, 2)} |`,
      `| Medium (ARC ≥4 and <8) | ${(bands.medium || 0).toLocaleString()} | ${moneyExact(bandExposure.medium)} | ${percentage(bandExposure.medium, totalExposure, 2)} |`,
      `| High (ARC ≥8) | ${(bands.high || 0).toLocaleString()} | ${moneyExact(bandExposure.high)} | ${percentage(bandExposure.high, totalExposure, 2)} |`,
      `| Missing ARC score | ${(bands.missing || 0).toLocaleString()} | ${moneyExact(bandExposure.missing)} | ${percentage(bandExposure.missing, totalExposure, 2)} |`,
      `Counts reconcile to **${rows.length.toLocaleString()} companies** and exposure reconciles to **${moneyExact(totalExposure)}**.`,
      `**Data used** — All ${rows.length.toLocaleString()} portfolio records; ARC score and ${scope === "ar" ? "outstanding AR" : "recommended limit"}.`,
      "**Method** — Used CrediArc's canonical boundaries only: Low <4, Medium 4–<8, High ≥8; missing scores are reported separately and retained in reconciliation.",
    ].join("\n\n");
  }

  if (/stress test/.test(clean)) {
    if (scope !== "ar") {
      const recommendedReductionMatch = clean.match(/(\d+(?:\.\d+)?)\s*%\s*(?:reduction|decrease|cut|drop)\s+in\s+recommended|(?:reduce|decrease|cut).{0,45}recommended.{0,30}?(\d+(?:\.\d+)?)\s*%/);
      const requestedIncreaseMatch = clean.match(/(\d+(?:\.\d+)?)\s*%\s*increase\s+in\s+requested|increase.{0,30}requested.{0,20}(\d+(?:\.\d+)?)\s*%/);
      const recommendedReduction = num(recommendedReductionMatch?.[1] || recommendedReductionMatch?.[2] || 0) / 100;
      const requestedIncrease = num(requestedIncreaseMatch?.[1] || requestedIncreaseMatch?.[2] || 0) / 100;
      const baselineCapacity = rows.reduce((sum, row) => sum + row.recommendedLimit, 0);
      const baselineDemand = rows.reduce((sum, row) => sum + row.amountRequested, 0);
      const stressedCapacity = baselineCapacity * (1 - recommendedReduction);
      const stressedDemand = baselineDemand * (1 + requestedIncrease);
      const baselineGap = rows.reduce((sum, row) => sum + Math.max(0, row.amountRequested - row.recommendedLimit), 0);
      const stressedGap = rows.reduce((sum, row) => sum + Math.max(0, row.amountRequested * (1 + requestedIncrease) - row.recommendedLimit * (1 - recommendedReduction)), 0);
      return [
        "## Prospect capacity stress test",
        `- **Baseline recommended capacity:** ${moneyExact(baselineCapacity)}`,
        `- **Stressed recommended capacity:** ${moneyExact(stressedCapacity)}`,
        `- **Capacity reduction:** ${moneyExact(baselineCapacity - stressedCapacity)} (${(recommendedReduction * 100).toFixed(1)}%)`,
        `- **Baseline requested demand:** ${moneyExact(baselineDemand)}`,
        `- **Stressed requested demand:** ${moneyExact(stressedDemand)}${requestedIncrease ? ` (${(requestedIncrease * 100).toFixed(1)}% increase)` : " (unchanged)"}`,
        `- **Row-level requested-limit shortfall:** ${moneyExact(baselineGap)} baseline → ${moneyExact(stressedGap)} stressed`,
        `**Data used** — All ${rows.length.toLocaleString()} prospects; requested and recommended limits.`,
        `**Method** — Stressed capacity = recommended limits × ${(1 - recommendedReduction).toFixed(2)}; stressed demand = requested amounts × ${(1 + requestedIncrease).toFixed(2)}. Shortfall is calculated company by company and then summed. No unstated stress factor was added. This is a scenario, not a forecast.`,
      ].join("\n\n");
    }
    const baseline = rows.reduce((sum, row) => sum + row.aging60 + row.aging90, 0);
    const stressed = baseline * 1.2;
    return [
      "## AR aging stress test",
      `A 20% increase in 60+ and 90+ balances raises overdue exposure from **${money(baseline)}** to **${money(stressed)}**, a change of **${money(stressed - baseline)}**.`,
      `**Data used** — All ${rows.length.toLocaleString()} AR records; aging60 and aging90.`,
      "**Method** — (aging60 + aging90) × 120%. This is a scenario, not a forecast.",
    ].join("\n\n");
  }
  return null;
}

const download = (name, content, type = "text/plain") => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
};

const artifactBlocks = (text) => {
  const blocks = [];
  const regex = /```aiarc\s*([\s\S]*?)```/gi;
  let match;
  while ((match = regex.exec(text || ""))) {
    try { blocks.push(JSON.parse(match[1])); } catch (error) { /* preserve malformed block as markdown */ }
  }
  return blocks;
};

function Artifact({ artifact }) {
  const data = Array.isArray(artifact?.data) ? artifact.data : [];
  const xKey = artifact?.xKey || "label";
  const yKeys = Array.isArray(artifact?.yKeys) ? artifact.yKeys : [artifact?.yKey || "value"];
  const save = () => download(`${String(artifact.title || "portfolio-analysis").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`, Papa.unparse(data), "text/csv");
  if (!data.length) return null;
  return <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
      <Box><Typography sx={{ fontWeight: 800, color: INK }}>{artifact.title || "Portfolio analysis"}</Typography>{artifact.methodology && <Typography sx={{ fontSize: 11.5, color: "#68758d" }}>{artifact.methodology}</Typography>}</Box>
      <Tooltip title="Download CSV"><IconButton size="small" onClick={save}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
    </Box>
    {artifact.type === "table" || artifact.type === "pivot" ? <Box sx={{ overflowX: "auto", maxHeight: 280 }}><Table size="small" stickyHeader><TableHead><TableRow>{Object.keys(data[0]).map((key) => <TableCell key={key} sx={{ fontWeight: 800 }}>{key}</TableCell>)}</TableRow></TableHead><TableBody>{data.slice(0, 200).map((row, index) => <TableRow key={index}>{Object.keys(data[0]).map((key) => <TableCell key={key}>{String(row[key] ?? "")}</TableCell>)}</TableRow>)}</TableBody></Table></Box> : <Box sx={{ width: "100%", height: 260 }}><ResponsiveContainer>
      {artifact.chartType === "line" ? <LineChart data={data}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey={xKey} /><YAxis /><ChartTooltip /><Legend />{yKeys.map((key, index) => <Line key={key} dataKey={key} stroke={[BLUE, "#12a150", "#e9a23b", "#e24b4a"][index % 4]} />)}</LineChart>
        : artifact.chartType === "pie" ? <PieChart><Pie data={data} dataKey={yKeys[0]} nameKey={xKey} fill={BLUE} label /><ChartTooltip /><Legend /></PieChart>
        : <BarChart data={data}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey={xKey} /><YAxis /><ChartTooltip /><Legend />{yKeys.map((key, index) => <Bar key={key} dataKey={key} fill={[BLUE, "#12a150", "#e9a23b", "#e24b4a"][index % 4]} />)}</BarChart>}
    </ResponsiveContainer></Box>}
  </Paper>;
}

const suggested = (scope) => [
  { label: "Concentration graph", icon: <InsertChartOutlinedIcon />, prompt: "Create a bar chart of the 10 largest portfolio exposures and explain the concentration risk." },
  { label: "Risk pivot", icon: <PivotTableChartIcon />, prompt: "Create a pivot table by country and ARC risk band with company count and exposure." },
  { label: "Stress test", icon: <ScienceOutlinedIcon />, prompt: scope === "ar"
    ? "Stress test a 20% increase in 60+ and 90+ overdue balances. Show baseline versus stressed exposure and the companies driving the change."
    : "Stress test a 20% reduction in recommended limits and a 15% increase in requested amounts. Show baseline versus stressed capacity and shortfall." },
];

export default function PortfolioCopilot({
  tenantId,
  tenantName,
  scope = "ar",
  visible = true,
  supplementalContext = null,
}) {
  const [open, setOpen] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [portfolio, setPortfolio] = useState({ rows: [], aggregates: {} });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const fileInput = useRef(null);
  const scroll = useRef(null);
  const historyKey = `crediarc:portfolio-ai:${tenantId || "tenant"}:${scope}`;
  const hasSupplementalContext = Boolean(
    supplementalContext && Object.keys(supplementalContext).length
  );
  const canAsk = hasPortfolioChatContext(portfolio.rows, files, supplementalContext);

  useEffect(() => { try { setMessages(JSON.parse(localStorage.getItem(historyKey) || "[]")); } catch (e) { setMessages([]); } }, [historyKey]);
  useEffect(() => { try { localStorage.setItem(historyKey, JSON.stringify(messages.filter((message) => !message.streaming))); } catch (e) { /* ignore */ } }, [historyKey, messages]);
  useEffect(() => { if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight; }, [messages, busy]);
  useEffect(() => { setPortfolio({ rows: [], aggregates: {} }); setFiles([]); }, [tenantId, scope]);

  const load = async () => {
    if (!tenantId || loadingData) return;
    setLoadingData(true);
    setError("");
    try {
      const [response, progress] = await Promise.all([
        listCompanySummaryData(tenantId),
        scope === "ar" ? Promise.resolve(null) : getBulkUploadProgress(tenantId).catch(() => null),
      ]);
      const raw = response?.data || [];
      setPortfolio({ ...compactPortfolio(raw, scope), metadata: buildPortfolioMetadata(raw, progress, scope) });
    }
    catch (e) { setError("Could not load the saved portfolio data."); }
    finally { setLoadingData(false); }
  };
  const openCopilot = () => {
    setOpen(true);
    if (!portfolio.rows.length && !hasSupplementalContext) load();
  };

  const portfolioContext = useMemo(() => buildPortfolioContext(portfolio.rows), [portfolio.rows]);
  const entity = useMemo(() => ({
    portfolioMode: true,
    tenantId,
    scope: scope === "ar" ? "Account Receivable" : "Prospects",
    scopeContract: portfolioScopeContract(scope),
    snapshotGeneratedAt: portfolio.metadata?.snapshotGeneratedAt || null,
    dataAsOf: portfolio.metadata?.dataAsOf || null,
    sourceSystem: portfolio.metadata?.sourceSystem || null,
    uploadJob: portfolio.metadata?.uploadJob || null,
    portfolioAggregates: portfolio.aggregates,
    portfolioAdvancedAnalytics: portfolio.advancedAnalytics,
    portfolioCoverage: {
      totalCompanies: portfolioContext.totalRows,
      rowDetailsIncluded: portfolioContext.includedRows,
      rowDetailsSummarized: portfolioContext.summarized,
      note: portfolioContext.summarized
        ? "Exact whole-portfolio aggregates are included. Row details are prioritized by requested-limit shortfall, exposure, and risk."
        : "All company row details are included.",
    },
    portfolioCompanies: portfolioContext.rows,
    supplementalContext,
    uploadedFiles: files.map(({ name, type, size, rows, includedRows, data }) => ({ name, type, size, rows, includedRows, data })),
  }), [portfolio.advancedAnalytics, portfolio.aggregates, portfolio.metadata, portfolioContext, files, scope, supplementalContext, tenantId]);

  const send = async (question) => {
    const clean = String(question || "").trim();
    if (!clean || busy || !canAsk) return;
    const base = [...compactConversationMessages(messages), { role: "user", content: clean }];
    const deterministicAnswer = answerPortfolioQuestion(clean, portfolio, scope);
    if (deterministicAnswer) {
      setMessages([...base, {
        role: "assistant",
        content: brandArcAI(deterministicAnswer),
        evidence: {
          question: clean,
          source: `Deterministic answer calculated from all ${portfolio.rows.length} loaded portfolio records.`,
        },
      }]);
      setError(""); setInput("");
      return;
    }
    const update = (content, streaming, evidence = null) => setMessages([...base, {
      role: "assistant",
      content: brandArcAI(content),
      streaming,
      ...(evidence && { evidence }),
    }]);
    setMessages([...base, { role: "assistant", content: "", streaming: true }]);
    setBusy(true); setError(""); setInput("");
    let turnEvidence = null;
    let turnEntity = entity;
    let allowPublicSources = false;
    try {
      const turnSpecificAnalytics = buildTurnSpecificAnalytics(clean, portfolio);
      const turnPortfolioContext = buildTurnPortfolioContext(clean, portfolio.rows, portfolioContext, turnSpecificAnalytics);
      allowPublicSources = allowsPublicCompanyResearch(clean, portfolio.rows);
      turnEntity = {
        ...entity,
        turnSpecificAnalytics,
        conversationEvidence: buildConversationEvidence(messages),
        portfolioCoverage: {
          totalCompanies: turnPortfolioContext.totalRows,
          rowDetailsIncluded: turnPortfolioContext.includedRows,
          rowDetailsSummarized: turnPortfolioContext.summarized,
          note: turnPortfolioContext.note || entity.portfolioCoverage?.note,
        },
        portfolioCompanies: turnPortfolioContext.rows,
      };
      turnEvidence = {
        question: clean,
        turnSpecificAnalytics,
        namedPortfolioRows: turnPortfolioContext.rows,
        coverageNote: turnPortfolioContext.note || entity.portfolioCoverage?.note,
      };
      const result = await runClaudeChatJob({
        tenantName,
        entityName: `${tenantName || "Tenant"} ${entity.scope} portfolio`,
        entity: turnEntity,
        messages: base,
        allowWeb: allowPublicSources,
      });
      update(result.reply, false, turnEvidence);
    } catch (jobError) {
      try {
        // Backward-compatible fallback during a rolling deployment, when an old
        // backend pod may not yet expose the polling endpoints.
        const status = jobError?.status || jobError?.response?.status;
        if (status !== 404) throw jobError;
        const result = await streamClaudeChat(
          { tenantName, entityName: `${tenantName || "Tenant"} ${entity.scope} portfolio`, entity: turnEntity, messages: base, allowWeb: allowPublicSources },
          { onDelta: (full) => update(full, true) }
        );
        update(result.reply, false, turnEvidence);
      }
      catch (e) {
        const status = e?.status || e?.response?.status;
        const message = e?.response?.data?.message || e?.message || "";
        const publicMessage = status === 413 || /too (large|much data)|payload|context length|request entity/i.test(message)
          ? "This request is too large for one AI analysis. Your portfolio is safe and unchanged. Ask about a segment, upload a filtered CSV, or split the file by region or risk band."
          : (brandArcAI(message) || "ArcAI is temporarily unavailable. Your portfolio data is safe—please try again.");
        // Preserve the user turn and a durable assistant failure message. Never
        // erase a streamed/queued answer and silently break conversational state.
        update(`I could not complete this analysis. ${publicMessage}`, false, turnEvidence);
        setError(publicMessage);
      }
    } finally { setBusy(false); }
  };

  const addFiles = async (event) => {
    const selected = Array.from(event.target.files || []);
    const parsed = [];
    for (const file of selected) {
      try {
        let data;
        if (/\.csv$/i.test(file.name)) data = Papa.parse(await file.text(), { header: true, skipEmptyLines: true }).data;
        else if (/\.(xlsx|xls)$/i.test(file.name)) { const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" }); data = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: "" }); }
        else if (/\.json$/i.test(file.name)) data = JSON.parse(await file.text());
        else data = (await file.text()).slice(0, 80000);
        const rows = Array.isArray(data) ? data.length : null;
        const sampled = Array.isArray(data) ? data.slice(0, UPLOAD_SAMPLE_ROWS) : data;
        parsed.push({
          name: file.name,
          type: file.type,
          size: file.size,
          rows,
          includedRows: Array.isArray(sampled) ? sampled.length : null,
          data: sampled,
          oversized: file.size > MAX_UPLOAD_SIZE || (Array.isArray(data) && data.length > UPLOAD_SAMPLE_ROWS),
        });
      } catch (e) { setError(`Could not read ${file.name}.`); }
    }
    setFiles((current) => [...current, ...parsed]);
    event.target.value = "";
  };

  const exportConversation = () => download(`arcai-${scope}-${new Date().toISOString().slice(0, 10)}.md`, messages.map((message) => `## ${message.role === "user" ? "Question" : "ArcAI"}\n\n${brandArcAI(message.content)}`).join("\n\n"), "text/markdown");
  if (!visible) return null;
  return <>
    <Tooltip title="Open ArcAI">
      <Fab aria-label="Open ArcAI" aria-expanded={open} onClick={openCopilot} sx={{ position: "fixed", right: { xs: 16, sm: 24 }, bottom: { xs: 16, sm: 24 }, zIndex: (theme) => theme.zIndex.speedDial, width: 56, height: 56, color: "#fff", background: "linear-gradient(135deg,#5b7cfa,#7c3aed)", boxShadow: "0 8px 24px rgba(64,52,180,.35)", transition: "transform 160ms ease, box-shadow 160ms ease", "&:hover": { background: "linear-gradient(135deg,#4568e8,#692bd1)", transform: "translateY(-2px)" } }}>
        <Box sx={{ position: "relative", display: "flex" }}><AutoAwesomeIcon /><Typography sx={{ position: "absolute", fontSize: 7, lineHeight: 1, fontWeight: 900, right: -1, bottom: -2 }}>AI</Typography></Box>
      </Fab>
    </Tooltip>
    <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, bgcolor: "#f7f9fd" } }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: 2, color: "#fff", background: "linear-gradient(135deg,#173461,#34246f)" }}><Box sx={{ display: "flex", justifyContent: "space-between" }}><Box sx={{ display: "flex", gap: 1.2, alignItems: "center" }}><AutoAwesomeIcon /><Box><Typography sx={{ fontWeight: 900 }}>ArcAI</Typography><Typography sx={{ fontSize: 11.5, opacity: .8 }}>Portfolio intelligence · tenant data first</Typography></Box></Box><IconButton aria-label="Close ArcAI" onClick={() => setOpen(false)} sx={{ color: "#fff" }}><CloseIcon /></IconButton></Box>
          <Box sx={{ display: "flex", gap: .7, mt: 1.3, flexWrap: "wrap" }}><Chip size="small" label={entity.scope} sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff" }} /><Chip size="small" label={portfolio.rows.length ? `${portfolio.rows.length} companies loaded` : hasSupplementalContext ? "Workspace summary loaded" : "0 companies loaded"} sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff" }} />{files.map((file) => <Chip key={file.name} size="small" label={file.name} onDelete={() => setFiles((current) => current.filter((item) => item !== file))} sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", "& .MuiChip-deleteIcon": { color: "#fff" } }} />)}</Box>
        </Box>
        {loadingData && <Box sx={{ p: 3, display: "flex", gap: 1, alignItems: "center" }}><CircularProgress size={18} /><Typography>Loading the saved portfolio…</Typography></Box>}
        {error && <Alert severity="error" onClose={() => setError("")} sx={{ m: 1.5 }}>{error}</Alert>}
        {portfolioContext.summarized && <Alert severity="info" sx={{ mx: 1.5, mt: error ? 0 : 1.5 }}>Large portfolio optimized: exact totals use all {portfolioContext.totalRows.toLocaleString()} companies; detailed rows include the {portfolioContext.includedRows.toLocaleString()} most relevant records.</Alert>}
        {files.some((file) => file.oversized) && <Alert severity="warning" sx={{ mx: 1.5, mt: 1 }}>Large upload optimized: ArcAI uses a 500-row sample. Filter or split the file by region or risk band when complete row-level analysis is required.</Alert>}
        {!loadingData && <Box sx={{ px: 1.5, py: 1, display: "flex", gap: .7, overflowX: "auto", bgcolor: "#fff", borderBottom: "1px solid #e5eaf2" }}>{suggested(scope).map((item) => <Chip key={item.label} icon={item.icon} label={item.label} onClick={() => send(item.prompt)} />)}</Box>}
        <Box ref={scroll} sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
          {!messages.length && !loadingData && <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography sx={{ fontWeight: 800, color: INK }}>Ask anything about this portfolio</Typography><Typography sx={{ fontSize: 13, color: "#65728a", mt: .5 }}>Create charts and pivots, run stress tests, compare exposures, investigate companies, or upload a spreadsheet for analysis. Every answer identifies the data and calculation used.</Typography></Paper>}
          {messages.filter((message) => !message.hidden).map((message, index) => <Box key={index} sx={{ display: "flex", justifyContent: message.role === "user" ? "flex-end" : "flex-start", my: 1 }}><Paper elevation={0} sx={{ maxWidth: "94%", p: 1.4, bgcolor: message.role === "user" ? "#e8efff" : "#fff", border: "1px solid #e3e8f1", borderRadius: 2 }}>
            {message.content ? <Box sx={{ fontSize: 13, lineHeight: 1.55, "& table": { borderCollapse: "collapse", width: "100%" }, "& th, & td": { border: "1px solid #dfe5ef", p: .5 } }}><ReactMarkdown remarkPlugins={[remarkGfm]}>{brandArcAI(message.content).replace(/```aiarc[\s\S]*?```/gi, "")}</ReactMarkdown>{artifactBlocks(message.content).map((artifact, artifactIndex) => <Artifact key={artifactIndex} artifact={artifact} />)}</Box> : <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}><CircularProgress size={14} /><Typography sx={{ fontSize: 12 }}>Analyzing saved tenant data…</Typography></Box>}
          </Paper></Box>)}
        </Box>
        <Box sx={{ p: 1.3, bgcolor: "#fff", borderTop: "1px solid #e1e7f0" }}><Box sx={{ display: "flex", gap: .7, mb: .8 }}><input ref={fileInput} hidden multiple type="file" accept=".csv,.xlsx,.xls,.json,.txt" onChange={addFiles} /><Button size="small" startIcon={<AttachFileIcon />} onClick={() => fileInput.current?.click()}>Upload data</Button><Button size="small" startIcon={<DownloadIcon />} onClick={exportConversation} disabled={!messages.length}>Download chat</Button><Button size="small" onClick={() => setMessages([])} disabled={!messages.length}>New chat</Button></Box><Box sx={{ display: "flex", gap: 1 }}><TextField fullWidth multiline maxRows={4} size="small" placeholder="Ask about your portfolio…" value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(input); } }} disabled={busy || loadingData} /><IconButton aria-label="Send to ArcAI" onClick={() => send(input)} disabled={busy || loadingData || !input.trim() || !canAsk} sx={{ bgcolor: BLUE, color: "#fff", "&:hover": { bgcolor: "#3f72d5" }, "&.Mui-disabled": { bgcolor: "#cbd5e5" } }}>{busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <ArrowUpwardIcon />}</IconButton></Box></Box>
      </Box>
    </Drawer>
  </>;
}
